import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Menubar } from "primereact/menubar";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import MozarkLogo from "../../assets/Images/mozark-logo.png";
import { AuthContext } from "../../AuthContext";

import "./Header.scss";
import ProgressBarLoader from "../ProgressBarLoader/ProgressBarLoader";
import { useAuthorization } from "../../hooks/useAuthPermissions";
import { AppPermissions } from "../../constants/permissions";

const Header = () => {
  const op = useRef(null);
  const navigate = useNavigate();
  const organization = localStorage.getItem("organization");
  const { token, logout } = useContext(AuthContext) || {};

  const [mozarkLogoWidth, setMozarkLogoWidth] = useState(150);
  const [orgLogoWidth, setOrgLogoWidth] = useState(150);
  const [OrgLogo, setOrgLogo] = useState(null);

  const isLoading = useSelector((state) => state?.loader?.loader);
  const { can } = useAuthorization();

  const confirmLogout = () => {
    confirmDialog({
      message: "Are you sure you want to logout?",
      header: "Logout Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: logout,
    });
  };

  const redirectToAdminPortal = () => {
    const adminPortalUrl = new URL(
      "users",
      process.env.REACT_APP_ADMIN_PORTAL_URL
    );
    window.open(adminPortalUrl.href, "_blank", "noopener,noreferrer");
  };

  const start = useMemo(
    () => (
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          src={MozarkLogo}
          alt="Mozark Logo"
          width={mozarkLogoWidth}
          style={{ marginRight: "10px", cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
      </div>
    ),
    [mozarkLogoWidth, navigate]
  );

  const end = useMemo(() => {
    let logoExists;

    try {
      require(`../../assets/Images/${OrgLogo}`);
      logoExists = true;
    } catch (error) {
      logoExists = false;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {logoExists ? (
          <img
            onClick={() => navigate("/")}
            src={require(`../../assets/Images/${OrgLogo}`)}
            alt="Organization_Logo"
            width={orgLogoWidth}
            style={{ marginRight: "10px" }}
          />
        ) : (
          <div
            style={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          >
            {organization}
          </div>
        )}

        {token && (
          <>
            <Button
              icon="pi pi-user"
              className="p-button-rounded p-button-text"
              style={{ color: "#7E8299" }}
              aria-label="Profile"
              onClick={(e) => op.current.toggle(e)}
            />
            <OverlayPanel ref={op}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {can(AppPermissions.USERS_CREATE) && (
                  <Button
                    label="Admin Portal"
                    icon="pi pi-user"
                    className="p-button-rounded p-button-text"
                    onClick={redirectToAdminPortal}
                  />
                )}

                <Button
                  label="Logout"
                  icon="pi pi-sign-out"
                  className="p-button-text"
                  onClick={confirmLogout}
                />
              </div>
            </OverlayPanel>
          </>
        )}
      </div>
    );
  }, [mozarkLogoWidth, op, navigate]);

  useEffect(() => {
    if (token) {
      if (organization) {
        setOrgLogo(`${organization}-logo.png`);
      } else {
        setOrgLogo(null);
      }

      const setLogoWidths = (logoSrc, setWidthCallback) => {
        const img = new Image();
        img.src = logoSrc;
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          setWidthCallback(40 * aspectRatio);
        };
      };

      if (OrgLogo) {
        setLogoWidths(OrgLogo, setOrgLogoWidth);
      }
      setLogoWidths(MozarkLogo, setMozarkLogoWidth);
    } else {
      setOrgLogo(null);
    }
  }, [token, OrgLogo]);

  return (
    <div className="headerContainer">
      <Menubar start={start} model={[]} end={end} />
      <ConfirmDialog />
      <ProgressBarLoader isLoading={isLoading} />
    </div>
  );
};

export default Header;
